import * as Sentry from "@sentry/react"
import { IS_PROD } from "../config/constants"
import { logger } from "../modules/logger/logger"
import { isBoolean, isNumber, isObject, isString } from "../modules/utils/utils"
import { twMerge } from "cn";

export type ClassProp =
  | {
      class: ClassValue
      className?: never
    }
  | { class?: never; className: ClassValue }
  | { class?: never; className?: never }
export type ClassPropKey = "class" | "className"
export type OmitUndefined<T> = T extends undefined ? never : T
export type StringToBoolean<T> = T extends "false" | "true" ? boolean : T

export type VariantProps<Component extends (...args: any)=> any> = Omit<
  OmitUndefined<Parameters<Component>[0]>,
  "class" | "className"
>

type ClassArray = ClassValue[]

type ClassDictionary = Record<string, boolean | null | number | string | undefined>
type ClassValue =
  | bigint
  | boolean
  | ClassArray
  | ClassDictionary
  | null
  | number
  | string
  | undefined
export function clsx(...inputs: ClassValue[]) {
  let i = 0
  let str = ""
  const len = inputs.length
  for (; i < len; i++) {
    const tmp = inputs[i]
    if (tmp) {
      const val = toVal(tmp)
      if (val) {
        if (str) str += " "
        str += val
      }
    }
  }
  return str
}
// ---------------------------------------------------------------------
// end clsx
// ---------------------------------------------------------------------

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
// ---------------------------------------------------------------------
// end of cn
// ---------------------------------------------------------------------

function toVal(mix: ClassValue): string {
  let str = ""

  if (isString(mix) || isNumber(mix)) {
    str += mix
  } else if (isObject(mix)) {
    if (Array.isArray(mix)) {
      const len = mix.length
      for (let k = 0; k < len; k++) {
        if (mix[k]) {
          const val = toVal(mix[k])
          if (val) {
            if (str) str += " "
            str += val
          }
        }
      }
    } else {
      for (const y in mix) {
        if (mix[y]) {
          if (str) str += " "
          str += y
        }
      }
    }
  }

  return str
}

const falsyToString = <T>(value: T)=>
  isBoolean(value) ? `${value}` : value === 0 ? "0" : value

export type CxOptions = Parameters<typeof clsx>
export type CxReturn = ReturnType<typeof clsx>

export const cx = clsx

type Config<T> = T extends ConfigSchema
  ? {
      compoundVariants?: (T extends ConfigSchema
        ? ClassProp & (ConfigVariants<T> | ConfigVariantsMulti<T>)
        : ClassProp)[]
      defaultVariants?: ConfigVariants<T>
      variants?: T
    }
  : never

type ConfigSchema = Record<string, Record<string, ClassValue>>
type ConfigVariants<T extends ConfigSchema> = {
  [Variant in keyof T]?: null | StringToBoolean<keyof T[Variant]> | undefined
}

type ConfigVariantsMulti<T extends ConfigSchema> = {
  [Variant in keyof T]?:
    | StringToBoolean<keyof T[Variant]>
    | StringToBoolean<keyof T[Variant]>[]
    | undefined
}

type Props<T> = T extends ConfigSchema
  ? ClassProp & ConfigVariants<T>
  : ClassProp

export const cva =
  <T>(base?: ClassValue, config?: Config<T>)=>
  (props?: Props<T>)=> {
    if (config?.variants == null) {
      return cx(base, props?.class, props?.className)
    }

    const { defaultVariants, variants } = config

    const getVariantClassNames = Object.keys(variants).map(
      (variant: keyof typeof variants)=> {
        // SAFETY: variant key is a keyof typeof props
        const variantProp = props?.[variant as keyof typeof props]
        const defaultVariantProp = defaultVariants?.[variant]

        if (variantProp === null) {
          return null
        }

        // SAFETY: falsyToString returns a valid variant key
        const variantKey = (falsyToString(variantProp) ||
          falsyToString(
            defaultVariantProp,
          )) as keyof (typeof variants)[typeof variant]

        return variants[variant][variantKey]
      },
    )

    const propsWithoutUndefined =
      props &&
      Object.entries(props).reduce(
        (acc, [key, value])=> {
          if (value === undefined || typeof value === "symbol") {
            return acc
          }

          acc[key] = value
          return acc
        },
        // SAFETY: reduce accumulator initialized as Record
        {} as Record<string, ClassValue>,
      )

    const getCompoundVariantClassNames = config?.compoundVariants?.reduce(
      (
        acc,
        { class: cvClass, className: cvClassName, ...compoundVariantOptions },
      )=> {
        const match = Object.entries(compoundVariantOptions).every(
          ([key, value])=>
            Array.isArray(value)
              ? value.includes(
                  {
                    ...defaultVariants,
                    ...propsWithoutUndefined,
                  }[key],
                )
              : {
                  ...defaultVariants,
                  ...propsWithoutUndefined,
                }[key] === value,
        )
        if (match) {
          acc.push(cvClass, cvClassName)
        }
        return acc
      },
      // SAFETY: reduce accumulator initialized as ClassValue array
      [] as ClassValue[],
    )

    return cx(
      base,
      getVariantClassNames,
      getCompoundVariantClassNames,
      props?.class,
      props?.className,
    )
  }

export const requestFullscreen = ()=> {
  if (!IS_PROD) {
    return
  }
  // iOS Safari has no Element.requestFullscreen; a hard call would throw inside
  // the click handler that closes the sidebar.
  if (!document.documentElement.requestFullscreen) {
    return
  }

  Sentry.captureMessage("Requesting fullscreen - requestFullscreen", "log")

  // The fullscreen promise must resolve before the orientation lock is
  // requested; calling lock() immediately rejects on some browsers. Both
  // failures are logged without a toast — a denied fullscreen (no user
  // gesture) is expected on route changes and must not surface to the user.
  const enterFullscreen = document.fullscreenElement
    ? Promise.resolve()
    : document.documentElement.requestFullscreen()

  enterFullscreen
    .then(()=> {
      if (!screen.orientation?.lock) return
      return screen.orientation.lock("landscape")
    })
    .catch((error: unknown)=> {
      logger({
        input: error instanceof Error ? error : new Error(String(error)),
        level: "error",
        toast: false,
      })
    })
}

export const isFullscreen = ()=> {
  return !!document.fullscreenElement
}

export const exitFullscreen = ()=> {
  if (!IS_PROD) {
    return
  }
  if (!document.exitFullscreen) {
    return
  }

  // Exiting fullscreen releases the orientation lock in most browsers, but an
  // explicit unlock() is what reliably returns the app to portrait on Android.
  // The promise rejection must be caught here: try/catch around the call does
  // not catch an async exitFullscreen() rejection.
  const leaveFullscreen = document.fullscreenElement
    ? document.exitFullscreen()
    : Promise.resolve()

  leaveFullscreen
    .then(()=> {
      return screen.orientation?.unlock?.()
    })
    .catch((error: unknown)=> {
      logger({
        input: error instanceof Error ? error : new Error(String(error)),
        level: "error",
        toast: false,
      })
    })
}

export const logMessage = (message: string, level?: Sentry.SeverityLevel)=> {
  Sentry.captureMessage(message, level ?? "debug")
}
export const logError = (error: unknown, label: string)=> {
  if (error instanceof Error) {
    Sentry.captureException(error, { tags: { label } })
    return
  }
  // eslint-disable-next-line anti-slop/no-runtime-typeof -- boundary type guard
  if (typeof error === "string") {
    Sentry.captureMessage(error, { tags: { label } })
    return
  }
  Sentry.captureMessage(String(error), { tags: { label } })
}
